import { useMemo } from 'react';
import { HeadLineType, Steps } from './worker-application-wizard.types';
import { useTranslation } from 'next-i18next';

export const useEmailRules = () => {
  const { t } = useTranslation();
  return useMemo(() => {
    return {
      required: true,
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: t('onboarding-page:must_be_a_valid_email'),
      },
    };
  }, [t]);
};

export const useBasicProfileInformation = () => {
  const { t } = useTranslation();
  const emailRules = useEmailRules();
  const requiredText = t('onboarding-page:this_field_is_required');
  return useMemo(
    () => [
      {
        key: 'name',
        title: t('onboarding-page:name'),
        type: 'text',
        placeholder: t('onboarding-page:your_name'),
        rules: {
          required: true,
        },
        message: requiredText,
      },
      {
        key: 'surname',
        title: t('onboarding-page:surname'),
        type: 'text',
        placeholder: t('onboarding-page:your_surname'),
        rules: {
          required: true,
        },
        message: requiredText,
      },
      {
        key: 'email',
        title: 'Email',
        type: 'email',
        placeholder: 'es. mariorossi@gmail.com',
        rules: { ...emailRules },
        message: requiredText,
      },
      {
        key: 'phone_number',
        title: t('onboarding-page:phone_number'),
        type: 'text',
        placeholder: t('onboarding-page:your_phone_number'),
        rules: {
          required: true,
        },
        message: requiredText,
      },
    ],
    [emailRules, requiredText, t]
  );
};
export const useWizardLastStepInputs = () => {
  const { t } = useTranslation();
  const placeholderText = t('common:paste_the_link');

  return useMemo(
    () => [
      {
        key: 'instagram_url',
        title: 'Instagram',
        type: 'text',
        placeholder: placeholderText,
        rules: {
          required: false,
        },
      },
      {
        key: 'facebook_url',
        title: 'Facebook',
        type: 'text',
        placeholder: placeholderText,
        rules: {
          required: false,
        },
      },
      {
        key: 'linkedin_url',
        title: 'Linkedin',
        type: 'text',
        placeholder: placeholderText,
        rules: {
          required: false,
        },
      },
      {
        key: 'twitter_url',
        title: 'Twitter',
        type: 'text',
        placeholder: placeholderText,
        rules: {
          required: false,
        },
      },
    ],
    [placeholderText]
  );
};
const useWizardInitialStep = () => {
  const { t } = useTranslation('onboarding-page');
  return useMemo(
    () => [
      {
        key: 'personal_info_1',
        headerKey: 'personal_info',
        completedPercentage: 0,
        info: t('knowing_who_you_are_and_where_to_contact'),
      },
      {
        key: 'personal_info_2',
        headerKey: 'personal_info',
        completedPercentage: 5,
        info: t('find_the_perfect_job_for_you_from_your_home'),
      },
      {
        key: 'personal_info_3',
        headerKey: 'personal_info',
        completedPercentage: 15,
        info: t('tell_us_how_you_prefer_to_be_contacted'),
      },
    ],
    [t]
  ) as Steps[];
};
const useStepTwo = () => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: 'moving_info',
        headerKey: 'moving_info',
        completedPercentage: 20,
        info: t('onboarding-page:jobs_in_line_with_travel_options'),
      },
    ],
    [t]
  ) as Steps[];
};
const useStepThree = () => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: 'step_3',
        headerKey: 'step_3_1',
        info: t('onboarding-page:will_recommend_the_most_suitable_positions'),
        completedPercentage: 25,
        isConfirmation: true,
        confirmationTitle: t('onboarding-page:have_already_work_done'),
        mainTitle: t('onboarding-page:your_experiences'),
      },
      {
        key: 'step_3_1_1',
        headerKey: 'step_3_1',
        info: t('onboarding-page:will_recommend_the_most_suitable_positions'),
        formHeaderTitle: t('common:experiences'),
        mainTitle: t('onboarding-page:your_experiences'),
        id: 'past_experiences',
        completedPercentage: 30,
      },
      {
        key: 'step_3_1_2',
        headerKey: 'step_3_1',
        info: t('onboarding-page:training_tells_us_a_lot_about_you'),
        formHeaderTitle: t('common:training'),
        completedPercentage: 35,
        mainTitle: t('onboarding-page:your_training'),
        id: 'education_em',
        canBeSkipped: true,
      },
      {
        key: 'step_3_1_3',
        headerKey: 'step_3_1',
        info: t('onboarding-page:training_tells_us_a_lot_about_you'),
        formHeaderTitle: t('common:certificate'),
        completedPercentage: 40,
        mainTitle: t('onboarding-page:your_training'),
        id: 'certificate',
        canBeSkipped: true,
      },
    ],
    [t]
  ) as Steps[];
};
const useStepFour = () => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: 'step_4',
        headerKey: 'step_4',
        info: t('onboarding-page:communication_is_the_basis_of_work'),
        formHeaderTitle: t('common:languages'),
        completedPercentage: 55,
        id: 'languages',
        mainTitle: t('onboarding-page:how_many_languages_do_you_speak'),
      },
    ],
    [t]
  ) as Steps[];
};
const useStepFifth = () => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: 'step_5',
        headerKey: 'step_5',
        info: t('onboarding-page:your_photos_who_you_actually_are'),
        formHeaderTitle: t('onboarding-page:your_media'),
        completedPercentage: 65,
        mainTitle: `${t('onboarding-page:smile_please')}! 📸`,
        canBeSkipped: true,
      },
    ],
    [t]
  ) as Steps[];
};
const useStepSix = () => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: 'step_6',
        headerKey: 'step_6',
        info: t('onboarding-page:we_rely_on_your_preferences'),
        formHeaderTitle: t('onboarding-page:how_urgent_are_you_in_looking_for_work'),
        completedPercentage: 70,
        mainTitle: t('onboarding-page:are_you_in_a_hurry'),
        canBeSkipped: false,
      },
    ],
    [t]
  ) as Steps[];
};
const useStepSeven = () => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: 'step_7',
        headerKey: 'step_7',
        info: t('onboarding-page:introducing_yourself_to_find_position_for_you'),
        formHeaderTitle: t('common:introduce_yourself'),
        completedPercentage: 75,
        mainTitle: t('common:tell_us_who_you_are'),
        canBeSkipped: true,
      },
    ],
    [t]
  ) as Steps[];
};
const useStepEight = () => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: 'step_8',
        headerKey: 'step_8',
        info: t('onboarding-page:listen_have_preferences_proposing_positions'),
        formHeaderTitle: t('common:preferences'),
        completedPercentage: 85,
        mainTitle: t('onboarding-page:tell_us_your_preferences'),
        canBeSkipped: true,
      },
    ],
    [t]
  ) as Steps[];
};
const useStepNine = () => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: 'step_9',
        headerKey: 'step_9',
        info: t('onboarding-page:take_opportunity_to_show_who_you_are'),
        formHeaderTitle: 'Social',
        completedPercentage: 100,
        mainTitle: t('common:be_social'),
        canBeSkipped: true,
      },
    ],
    [t]
  ) as Steps[];
};
export const useWizardSteps = () => {
  const wizardInitialStep = useWizardInitialStep();
  const stepTwo = useStepTwo();
  const stepThree = useStepThree();
  const stepFour = useStepFour();
  const stepFifth = useStepFifth();
  const stepSix = useStepSix();
  const stepSeven = useStepSeven();
  const stepEight = useStepEight();
  const stepNine = useStepNine();
  return useMemo(
    () => [
      ...wizardInitialStep,
      ...stepTwo,
      ...stepThree,
      ...stepFour,
      ...stepFifth,
      ...stepSix,
      ...stepSeven,
      ...stepEight,
      ...stepNine,
    ],
    [stepEight, stepFifth, stepFour, stepNine, stepSeven, stepSix, stepThree, stepTwo, wizardInitialStep]
  ) as Steps[];
};
// --> common headlines
export const useHeadLines = () => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: 'personal_info',
        mainTitle: t('onboarding-page:tell_us_who_you_are'),
        formHeaderTitle: t('onboarding-page:on_you'),
      },
      {
        key: 'moving_info',
        mainTitle: t('onboarding-page:how_do_you_get_around'),
        formHeaderTitle: t('onboarding-page:how_you_move'),
      },
    ],
    [t]
  ) as HeadLineType[];
};
